import { useEffect, useRef, useState } from "react";

/**
 * Animated number counter. Counts from current value to `value` when the
 * element enters the viewport, and re-animates if `value` later changes
 * (so API-driven stats update smoothly). Respects prefers-reduced-motion.
 *
 *   <CountUp value={5254} prefix="$" duration={1400} />
 */
export default function CountUp({
  value = 0,
  duration = 1200,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
  ...rest
}) {
  const target = Number(value) || 0;
  const [shown, setShown] = useState(0);
  const ref = useRef(null);
  const inViewRef = useRef(false);
  const rafRef = useRef(null);
  const shownRef = useRef(0);
  const targetRef = useRef(target);

  // Track latest `shown` so we can start an animation from the current value.
  shownRef.current = shown;
  // Track latest `target` so the IntersectionObserver callback (which closes
  // over the mount-time value) can read the up-to-date number when it fires.
  targetRef.current = target;

  const animateTo = (next, fromOverride) => {
    const from = fromOverride !== undefined ? fromOverride : shownRef.current;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setShown(next);
      return;
    }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(from + (next - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else {
        setShown(next);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // Observer: flip inViewRef once, then run the first animation from 0.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      inViewRef.current = true;
      animateTo(targetRef.current, 0);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !inViewRef.current) {
            inViewRef.current = true;
            animateTo(targetRef.current, 0);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When target changes AFTER we're already in view, re-animate smoothly
  // from the current shown value to the new target.
  useEffect(() => {
    if (!inViewRef.current) return;
    animateTo(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const display = shown.toLocaleString("en-NZ", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className} {...rest}>
      {prefix}{display}{suffix}
    </span>
  );
}
